import React, { useEffect, useState } from 'react';
import { getDashboardStats, getSystemAnalytics } from '../../api/agentApi';
import { getAllTickets } from '../../api/ticketApi';
import StatsCard from '../../components/Dashboard/StatsCard';
import TicketList from '../../components/Tickets/TicketList';
import CreateTicketModal from '../../components/Tickets/CreateTicketModal';
import Loading from '../../components/Common/Loading';
import ErrorMessage from '../../components/Common/ErrorMessage';

export default function ManagerDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsRes, analyticsRes, ticketsRes] = await Promise.all([
        getDashboardStats(),
        getSystemAnalytics(),
        getAllTickets(statusFilter)
      ]);
      
      setStats(statsRes.stats);
      setAnalytics(analyticsRes.analytics);
      setTickets(ticketsRes.tickets);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketCreated = (newTicket) => {
    fetchData(); // Refresh tickets list
    alert(`Ticket ${newTicket.ticket_number} created successfully!`);
  };

  if (loading && !stats) {
    return <Loading message="Loading manager dashboard..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchData} />;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Manager Dashboard</h1>
          <p>Complete system overview and analytics</p>
        </div>
        <button 
          className="btn-create-ticket"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Ticket
        </button>
      </div>

      <div className="stats-grid">
        <StatsCard
          title="Total Tickets"
          value={stats?.total_tickets || 0}
          icon="🎫"
          color="#667eea"
        />
        <StatsCard
          title="Open Tickets"
          value={stats?.open_tickets || 0}
          icon="📂"
          color="#ff9800"
        />
        <StatsCard
          title="Total Agents"
          value={stats?.total_agents || 0}
          icon="👥"
          color="#9c27b0"
        />
        <StatsCard
          title="Online Agents"
          value={stats?.online_agents || 0}
          icon="🟢"
          color="#4caf50"
        />
      </div>

      {analytics && (
        <div className="analytics-section">
          <div className="analytics-card">
            <h3>By Category</h3>
            <div className="analytics-list">
              {analytics.by_category?.map((item) => (
                <div key={item.category} className="analytics-item">
                  <span>{item.category}</span>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="analytics-card">
            <h3>By Priority</h3>
            <div className="analytics-list">
              {analytics.by_priority?.map((item) => (
                <div key={item.priority} className="analytics-item">
                  <span>{item.priority}</span>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-section">
        <TicketList
          tickets={tickets}
          loading={false}
          onStatusFilter={setStatusFilter}
        />
      </div>

      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleTicketCreated}
      />
    </div>
  );
}
